import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Landing from './pages/Landing';
import { isWheelPath } from './utils/wheelRedirect';

const Login = React.lazy(() => import('./pages/Login'));
const UserDashboard = React.lazy(() => import('./pages/UserDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const WheelSpinner = React.lazy(() => import('./pages/WheelSpinner'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));

// The marketing homepage and admin dashboard are the B2B (owner-facing)
// surfaces — they get a darker, muted accent than the bright consumer
// wheel/prize screens. Scoped here so the shared Navbar picks it up too.
//
// The QR-scan customer journey (the wheel itself, and the login screen it
// redirects through) hides the site nav entirely — a customer who just
// scanned a code to spin a wheel shouldn't be able to tap "Accueil" and
// wander into the owner marketing page, or lose the "I'm a customer"
// context and land on a login screen asking them to pick a role.
function AppShell() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isB2BRoute = pathname === '/' || pathname.startsWith('/admin');
  const isCustomerFlow = isWheelPath(pathname) || (pathname === '/login' && isWheelPath(searchParams.get('from')));

  return (
    <div className={`min-h-screen ${isB2BRoute ? 'b2b-theme' : ''}`} style={{ backgroundColor: '#FAF8F3' }}>
      {!isCustomerFlow && <Navbar />}
      <main className={isCustomerFlow ? '' : 'pb-16 md:pb-0'}>
        <div className="w-full">
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen">
              <div className="w-10 h-10 rounded-full border-4 border-[#FF4D2E] border-t-transparent animate-spin" />
            </div>
          }>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <UserDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <AdminDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/spin/:establishmentId"
                element={
                  <PrivateRoute>
                    <WheelSpinner />
                  </PrivateRoute>
                }
              />
              <Route
                path="/wheel/:establishmentId"
                element={
                  <PrivateRoute>
                    <WheelSpinner />
                  </PrivateRoute>
                }
              />

              {/* Legal */}
              <Route path="/mentions-legales" element={<LegalPage />} />
              <Route path="/cgu" element={<LegalPage />} />
              <Route path="/reglement" element={<LegalPage />} />
              <Route path="/confidentialite" element={<LegalPage />} />
              <Route path="/cookies" element={<LegalPage />} />
              <Route path="/contact" element={<LegalPage />} />
            </Routes>
          </Suspense>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </Router>
  );
}

export default App;
